/**
 * @file Thin wrappers around the Cloud Functions callables. Page modules
 * should call these instead of using `httpsCallable` directly so we have one
 * place to retry, log, or swap to a different transport.
 */

import { httpsCallable } from 'firebase/functions';
import { fns } from './firebase.js';
import { isTestMode } from './test-mode.js';

// Stamp every side-effecting callable with the caller's test-mode flag.
// The server re-derives nothing it doesn't have to: it reads this boolean
// per-request and routes its writes to the shared test_ namespace + no-ops
// Slack when true. Injecting here (one chokepoint) means no individual
// call site can forget to pass it. Sync callables are exempt — they're
// disabled in the UI during test mode rather than namespaced.
function withTestMode(data = {}) {
  return { ...data, testMode: isTestMode() };
}

export async function syncBambooHR() {
  // List-only sync (one HTTP to BambooHR's custom-report endpoint).
  // Used by the nightly cron + the Sync everything button.
  const fn = httpsCallable(fns, 'syncBambooHR', { timeout: 9 * 60 * 1000 });
  const res = await fn({});
  return res.data;
}

export async function syncBambooAssets() {
  // Per-employee customAssets fan-out (~300 parallel HTTP calls). NOT in
  // the nightly cron — admins trigger this from Settings when phone-tariff
  // state actually needs to be refreshed (Finance just updated a Sim Card,
  // a new joiner's tariff was issued, etc.). Cheap to skip when the data
  // hasn't changed.
  const fn = httpsCallable(fns, 'syncBambooAssets', { timeout: 9 * 60 * 1000 });
  const res = await fn({});
  return res.data;
}

export async function syncFlexiDevices() {
  // First Flexi sync writes ~1000 device docs + ~1000 device_view docs;
  // give it the same generous timeout as bamboo.
  const fn = httpsCallable(fns, 'syncFlexiDevices', { timeout: 9 * 60 * 1000 });
  const res = await fn({});
  return res.data;
}

export async function syncMerakiDevices() {
  // Meraki SM scan + the reconcile step run in well under a minute even for
  // ~1000 devices, but use the same generous timeout for parity.
  const fn = httpsCallable(fns, 'syncMerakiDevices', { timeout: 9 * 60 * 1000 });
  const res = await fn({});
  return res.data;
}

export async function syncIruDevices() {
  // IRU (formerly Kandji) MDM device pull — sibling of syncMerakiDevices,
  // running in parallel during the Meraki → IRU transition.
  const fn = httpsCallable(fns, 'syncIruDevices', { timeout: 9 * 60 * 1000 });
  const res = await fn({});
  return res.data;
}

export async function syncTestingDevices() {
  // Reads the "Testing devices — Unified" Google Sheet and mirrors every
  // row into /testing_devices. Cheap (one sheet, ~hundreds of rows) but
  // network-bound, so keep the same generous timeout as the other syncs.
  const fn = httpsCallable(fns, 'syncTestingDevices', { timeout: 9 * 60 * 1000 });
  const res = await fn({});
  return res.data;
}

export async function materialiseDevices() {
  // Materialise joins /flexi_devices + /meraki_devices + /iru_devices +
  // /testing_devices + /employees and rebuilds /devices + /device_view.
  // Fast — pure Firestore reads + writes, no external API calls — but keep
  // the long timeout for safety.
  const fn = httpsCallable(fns, 'materialiseDevices', { timeout: 9 * 60 * 1000 });
  const res = await fn({});
  return res.data;
}

// syncEverything (the ordered full-pipeline run) lives in lib/sync-stages.js
// with the stage registry — one entry there drives the pipeline, the
// Settings buttons, toasts, status lines and audit diffs together.

export async function syncFormResponses() {
  const fn = httpsCallable(fns, 'syncFormResponses', { timeout: 9 * 60 * 1000 });
  const res = await fn({});
  return res.data;
}

/**
 * Server-side hand-over of a GEAR-ONLY peripheral: creates the device in
 * /devices + /device_view AND the /handovers ledger row in a single batch, so
 * the employee sees it on /me without waiting for the nightly sync. Laptops
 * and phones are refused server-side — those are a `register` gear action
 * (submitGearAction) that goes through Finance and Abra.
 */
export async function handoverDevice(payload) {
  const fn = httpsCallable(fns, 'handoverDevice');
  // Idempotency key: a transient SDK/network retry of this exact call
  // collapses to one device + one handover row server-side.
  const requestId = (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const res = await fn(withTestMode({ ...payload, requestId }));
  return res.data;
}

/**
 * Remove a Gear-only peripheral (keyboard / mouse / charger / …) entirely —
 * deletes both /devices and /device_view in one server-side batch. Used by the
 * device-detail "Remove" button. These peripherals aren't tracked anywhere
 * once returned, so a `retired` stub would just be clutter. The server refuses
 * non-gear devices. Returns { ok, removed, deviceId }.
 */
export async function removeGearDevice({ deviceId }) {
  const fn = httpsCallable(fns, 'removeGearDevice');
  const res = await fn(withTestMode({ deviceId }));
  return res.data;
}

/**
 * Mark an MDM orphan (IRU / Meraki, not in Abra) as the person's own laptop,
 * or back to a company device. Server-side so /device_view stays in step.
 * Returns { ok, personal, updated }.
 */
export async function setPersonalDevice({ deviceId, personal }) {
  const fn = httpsCallable(fns, 'setPersonalDevice');
  const res = await fn(withTestMode({ deviceId, personal }));
  return res.data;
}

/**
 * Close an offboarding that was handled out-of-band — sets employee to
 * `done`, sticks a `manuallyMarkedDone` flag so the Bamboo sync won't
 * re-promote them, and cancels every still-open device decision in one
 * atomic batch. Returns the cancelled device list so the UI can toast a
 * useful summary.
 */
export async function dismissOffboarding({ employeeId, note = null }) {
  const fn = httpsCallable(fns, 'dismissOffboarding');
  const res = await fn(withTestMode({ employeeId, note }));
  return res.data;
}

/**
 * Finish onboarding without handing anything over — the onboarding twin of
 * dismissOffboarding. Moves the employee to `active`, sets the sticky
 * `onboardingDismissed` flag so the sync won't re-queue them, and cancels any
 * open onboarding items. For joiners who bring their own gear or never filled
 * the form.
 */
export async function finishOnboarding({ employeeId, note = null }) {
  const fn = httpsCallable(fns, 'finishOnboarding');
  const res = await fn(withTestMode({ employeeId, note }));
  return res.data;
}

/**
 * Submit a device action (handover / takeback / transfer / make_testing /
 * sell / loot / dispose) into the "pending Abra" queue. Writes a pending_changes
 * row, posts to Slack, audits. The reconciliation pass on the next sync
 * detects when Abra catches up and marks the row reconciled.
 *
 * @param payload.action     one of 'handover'|'takeback'|'transfer'|'make_testing'|'sell'|'loot'|'dispose'
 * @param payload.deviceId   the /devices doc id
 * @param payload.payload    action-specific data ({ fromHolder, toHolder, saleData, note })
 */
export async function submitGearAction(payload) {
  const fn = httpsCallable(fns, 'submitGearAction');
  // Attach a stable idempotency key. The server uses it as the
  // pending_changes doc id, so a transient SDK/network retry of THIS call
  // (same payload) collapses to a single pending row + single Finance ping
  // instead of duplicating. A fresh id per logical submit is correct;
  // double-clicks are additionally guarded by busy-state in the UI.
  const requestId = (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const res = await fn(withTestMode({ ...payload, requestId }));
  return res.data;
}

/** Send a device to Loot through the IT Hub: Loot gets a draft listing
 *  (price and condition are set there), the device becomes for_sale, and
 *  Finance hears it's on sale. Returns { listingId, url, ref, slack }. */
export async function sendToLoot({ deviceId, note = null }) {
  const fn = httpsCallable(fns, 'sendToLoot', { timeout: 60 * 1000 });
  const res = await fn(withTestMode({ deviceId, note }));
  return res.data;
}

/** Cancel a pending change (admin mistake / went out-of-band). Posts a
 *  "Cancelled" follow-up to Slack so Finance doesn't keep working on it. */
export async function cancelGearAction({ pendingId, reason = null }) {
  const fn = httpsCallable(fns, 'cancelGearAction');
  const res = await fn(withTestMode({ pendingId, reason }));
  return res.data;
}

/** Settings page "Test" button — posts a hello message via the configured
 *  Slack webhook so admins can verify the destination before going live. */
export async function testSlack() {
  const fn = httpsCallable(fns, 'testSlack');
  const res = await fn(withTestMode({}));
  return res.data;
}

/** Settings → Slack: on/off, label and the webhook (masked — the URL itself
 *  stays in Secret Manager). */
export async function getSlackSettings() {
  const fn = httpsCallable(fns, 'getSlackSettings');
  const res = await fn(withTestMode({}));
  return res.data;
}

/** Save Slack settings. `webhookUrl` only when the admin typed a new one. */
export async function saveSlackSettings({ webhookUrl = null, destinationLabel = null, enabled = true }) {
  const fn = httpsCallable(fns, 'saveSlackSettings');
  const res = await fn(withTestMode({ webhookUrl, destinationLabel, enabled }));
  return res.data;
}

/** Manual "Retry failed Slack" — drains the queue right now (also runs
 *  every 30 minutes via scheduled cron, but admins can force it). */
export async function retrySlackNow() {
  const fn = httpsCallable(fns, 'retrySlackNow');
  const res = await fn(withTestMode({}));
  return res.data;
}

/** Reseed the SHARED test sandbox (test_* collections) — wipes everything
 *  test_-prefixed (incl. legacy per-user sandboxes) and writes fresh mock
 *  data for all admins. Admin-only. */
export async function seedTestData() {
  const fn = httpsCallable(fns, 'seedTestData', { timeout: 2 * 60 * 1000 });
  const res = await fn({});
  return res.data;
}

